// Clones the pristine demo dataset into the visitor's own namespace.
//
// Reads come from artifacts/{SEED_NS}/… (admin-written, read-only to visitors)
// and writes go to artifacts/{DEMO_NS}/…, so each visitor explores a full hotel
// portal and anything they post, edit or delete stays in their own sandbox.
// Media is NOT copied — gallery and learning documents carry absolute URLs, so
// every session shares the same underlying Storage objects and only the small
// Firestore documents are duplicated.

import {
  collection,
  doc,
  getDoc,
  getDocs,
  setDoc,
  writeBatch,
} from "firebase/firestore";
import {
  DATE_KEYED_COLLECTIONS,
  isYmd,
  localYmd,
  shiftValue,
  retimeDoc,
} from "./retime";
import {
  DEMO_NS,
  SEED_NS,
  SEED_MARKER_DOC,
  SEED_COLLECTIONS,
  SEED_SUBCOLLECTIONS,
  SANDBOX_TTL_DAYS,
  DEMO_LOGIN_ID,
} from "./demoConfig";

const BATCH_LIMIT = 400; // Firestore caps a batch at 500 writes.

const dataPath = (ns, col) => `artifacts/${ns}/public/data/${col}`;

function expiryTimestamp() {
  return new Date(Date.now() + SANDBOX_TTL_DAYS * 24 * 60 * 60 * 1000);
}

// Every cloned document carries _demoExpiresAt so the Firestore TTL policies can
// reclaim abandoned sandboxes without a scheduled function.
function stamp(data, extra) {
  return { ...data, _demoExpiresAt: expiryTimestamp(), ...extra };
}

class BatchWriter {
  constructor(db) {
    this.db = db;
    this.batch = writeBatch(db);
    this.pending = 0;
    this.total = 0;
  }
  async set(ref, data) {
    this.batch.set(ref, data);
    this.pending += 1;
    this.total += 1;
    if (this.pending >= BATCH_LIMIT) await this.flush();
  }
  async flush() {
    if (this.pending === 0) return;
    await this.batch.commit();
    this.batch = writeBatch(this.db);
    this.pending = 0;
  }
}

// Clone-time date shifting lives in ./retime.js — see the comment there for why
// the seed's dates move with the calendar.

const DAY_MS = 86400000;

// Whole-day offset only: shifting by a partial day would move a 03:20 night
// audit onto the wrong calendar date.
async function readOffsetMs(db) {
  try {
    const snap = await getDoc(doc(db, dataPath(SEED_NS, "appMeta"), "seedMeta"));
    const anchor = snap.exists() ? snap.data().anchorDate : null;
    if (!isYmd(anchor)) return 0;
    const a = new Date(anchor + "T12:00:00");
    const today = new Date(localYmd(new Date()) + "T12:00:00");
    return Math.round((today - a) / DAY_MS) * DAY_MS;
  } catch (_) {
    return 0;
  }
}

async function cloneCollections(db, writer, onProgress, offsetMs) {
  let done = 0;
  const tick = () => onProgress?.(++done, SEED_COLLECTIONS.length);

  // Fetch every collection concurrently — done sequentially this was ~35 round
  // trips and the splash sat there for 10-25s.
  const reads = await Promise.all(
    SEED_COLLECTIONS.map(async (col) => {
      const snap = await getDocs(collection(db, dataPath(SEED_NS, col)));
      const subs = await Promise.all(
        SEED_SUBCOLLECTIONS.filter(([parent]) => parent === col).flatMap(
          ([, sub]) =>
            snap.docs.map(async (d) => ({
              sub,
              parentId: d.id,
              snap: await getDocs(
                collection(db, `${dataPath(SEED_NS, col)}/${d.id}/${sub}`)
              ),
            }))
        )
      );
      tick();
      return { col, snap, subs };
    })
  );

  for (const { col, snap, subs } of reads) {
    for (const d of snap.docs) {
      // Some collections are keyed BY date, so the id moves with the content.
      const id = DATE_KEYED_COLLECTIONS.includes(col)
        ? shiftValue(d.id, offsetMs)
        : d.id;
      await writer.set(
        doc(db, dataPath(DEMO_NS, col), id),
        stamp(retimeDoc(col, d.data(), offsetMs))
      );
    }
    for (const { sub, parentId, snap: subSnap } of subs) {
      const pid = DATE_KEYED_COLLECTIONS.includes(col)
        ? shiftValue(parentId, offsetMs)
        : parentId;
      for (const sd of subSnap.docs) {
        await writer.set(
          doc(db, `${dataPath(DEMO_NS, col)}/${pid}/${sub}`, sd.id),
          stamp(retimeDoc(`${col}/${sub}`, sd.data(), offsetMs))
        );
      }
    }
  }
}

// Staff profiles live at artifacts/{ns}/users/{userId}/profiles/{profileId}.
// Each clone is stamped with _ns so the scoped collectionGroup("profiles")
// wrapper in App.js only ever sees this session's staff.
// The News Feed opens on a "Daily Highlights" panel listing today's birthdays
// and work anniversaries. Fixed seed dates would leave it empty on all but two
// days a year, so the flagged colleagues are moved onto the visitor's own today
// (keeping the original year, so ages and years of service still read sensibly).
function retimeHighlights(profile) {
  const today = new Date();
  const md = `${String(today.getMonth() + 1).padStart(2, "0")}-${String(
    today.getDate()
  ).padStart(2, "0")}`;
  const out = { ...profile };
  if (profile.birthdayToday && typeof profile.birthDate === "string") {
    out.birthDate = `${profile.birthDate.slice(0, 4)}-${md}`;
  }
  if (profile.anniversaryToday && typeof profile.hireDate === "string") {
    out.hireDate = `${profile.hireDate.slice(0, 4)}-${md}`;
  }
  return out;
}

async function cloneProfiles(db, writer, offsetMs) {
  const seedIndexRef = doc(db, dataPath(SEED_NS, "appMeta"), "profileIndex");
  const seedIndex = await getDoc(seedIndexRef);
  if (!seedIndex.exists()) {
    throw new Error(
      "demo seed is missing appMeta/profileIndex — run scripts/seed-demo.js"
    );
  }
  const entries = seedIndex.data().entries || [];

  const sources = await Promise.all(
    entries.map(async ({ userId, profileId }) => ({
      userId,
      profileId,
      snap: await getDoc(
        doc(db, `artifacts/${SEED_NS}/users/${userId}/profiles`, profileId)
      ),
    }))
  );

  let loginPath = null;
  for (const { userId, profileId, snap } of sources) {
    if (!snap.exists()) continue;
    await writer.set(
      doc(db, `artifacts/${DEMO_NS}/users/${userId}/profiles`, profileId),
      stamp(retimeDoc("profiles", retimeHighlights(snap.data()), offsetMs), {
        _ns: DEMO_NS,
      })
    );
    if (snap.data().uniqueId === DEMO_LOGIN_ID) {
      loginPath = `artifacts/${DEMO_NS}/users/${userId}/profiles/${profileId}`;
    }
  }
  return loginPath;
}

/**
 * Ensures this visitor's namespace holds a full copy of the demo hotel.
 * Idempotent: the marker document is written last, so an interrupted clone is
 * simply redone on the next visit.
 *
 * @returns {Promise<{cloned: boolean, loginProfilePath: string|null}>}
 */
export async function ensureSandboxSeeded(db, onProgress) {
  const markerRef = doc(db, dataPath(DEMO_NS, "appMeta"), SEED_MARKER_DOC);
  const marker = await getDoc(markerRef);
  if (marker.exists()) {
    return { cloned: false, loginProfilePath: marker.data().loginProfilePath || null };
  }

  const offsetMs = await readOffsetMs(db);
  const writer = new BatchWriter(db);
  await cloneCollections(db, writer, onProgress, offsetMs);
  const loginProfilePath = await cloneProfiles(db, writer, offsetMs);
  await writer.flush();

  await setDoc(
    markerRef,
    stamp({
      seededAt: new Date().toISOString(),
      seedNamespace: SEED_NS,
      documentsCopied: writer.total,
      loginProfilePath,
    })
  );

  return { cloned: true, loginProfilePath };
}
